import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { observable, syncState } from "@legendapp/state";
import { useValue } from "@legendapp/state/react";
import { normalizeSql } from "@repo/shared/helpers/normalize-sql";
import { AvatarDisplay } from "@repo/ui/components/avatar-display";
import { Icon } from "@repo/ui/components/icon";
import { ScrollFade } from "@repo/ui/components/scroll-fade";
import { preloadSqlEditor } from "@repo/ui/components/sql-editor";
import { Token } from "@repo/ui/components/token";
import { useSidebarMobileClick } from "@repo/ui/hooks/use-sidebar-mobile-click";
import { Input } from "@repo/ui/input";
import {
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@repo/ui/ui/sidebar";
import { Skeleton } from "@repo/ui/ui/skeleton";
import { createLink } from "@tanstack/react-router";
import { useRef, useState } from "react";

import { DatasetOptions } from "~/components/dataset-options";
import {
  DatasetUsageTooltip,
  useBlocksUsingDataset,
} from "~/components/dataset-usage-tooltip";
import { VerifiedTooltip } from "~/components/verified-tooltip";
import { prefetchDataset, usePrefetchHandlers } from "~/stores/board-payloads";
import { datasets$ } from "~/stores/datasets";
import { reorderDataset } from "~/stores/datasets/datasets.actions";

const SidebarMenuButtonLink = createLink(SidebarMenuButton);

/**
 * Hover prefetch for a dataset row: warm both the Arrow payload and the Monaco
 * runtime the dataset page's SQL editor depends on, so navigating to the
 * dataset feels instant. Module-level for a stable reference across renders.
 */
const prefetchDatasetRow = (id: string) => {
  preloadSqlEditor();
  prefetchDataset(id);
};

const draftDatasetIds$ = observable<string[]>(() => {
  const datasetIds = Object.keys(datasets$);
  return datasetIds
    .filter(
      (id) => datasets$[id].private.get() && !datasets$[id].favorite.get(),
    )
    .sort((a, b) => {
      const orderA = datasets$[a].order.get() ?? "a0";
      const orderB = datasets$[b].order.get() ?? "a0";
      return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
    });
});

const publishedDatasetIds$ = observable<string[]>(() => {
  const datasetIds = Object.keys(datasets$);
  return datasetIds
    .filter(
      (id) => !datasets$[id].private.get() && !datasets$[id].favorite.get(),
    )
    .sort((a, b) => {
      const orderA = datasets$[a].order.get() ?? "a0";
      const orderB = datasets$[b].order.get() ?? "a0";
      return orderA < orderB ? -1 : orderA > orderB ? 1 : 0;
    });
});

/** True when at least one draft (private) dataset exists. */
export const hasDraftDatasets$ = observable(
  () => draftDatasetIds$.get().length > 0,
);

/** True when at least one shared (non-private) dataset exists. */
export const hasSharedDatasets$ = observable(
  () => publishedDatasetIds$.get().length > 0,
);

/** Draft (private) datasets list for the sidebar. */
export const DraftDatasetsList = () => {
  const isLoading = useValue(() => !syncState(datasets$).isPersistLoaded.get());
  const draftIds = useValue(draftDatasetIds$);
  const [localIds, setLocalIds] = useState<string[] | null>(null);
  const displayIds = localIds ?? draftIds;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = displayIds.indexOf(active.id as string);
      const newIndex = displayIds.indexOf(over.id as string);
      const newOrder = arrayMove(displayIds, oldIndex, newIndex);
      setLocalIds(newOrder);
      reorderDataset(active.id as string, newIndex, displayIds);
    }
    setTimeout(() => setLocalIds(null), 100);
  };

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (draftIds.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={displayIds}
        strategy={verticalListSortingStrategy}
      >
        <ScrollFade
          className="no-scrollbar -m-1 max-h-50 p-1"
          fromClassName="from-sidebar"
        >
          <SidebarMenu>
            {displayIds.map((id) => (
              <DatasetItem key={id} id={id} />
            ))}
          </SidebarMenu>
        </ScrollFade>
      </SortableContext>
    </DndContext>
  );
};

/** Published (shared) datasets list for the sidebar. */
export const PublishedDatasetsList = () => {
  const isLoading = useValue(() => !syncState(datasets$).isPersistLoaded.get());
  const publishedIds = useValue(publishedDatasetIds$);
  const [localIds, setLocalIds] = useState<string[] | null>(null);
  const displayIds = localIds ?? publishedIds;

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const oldIndex = displayIds.indexOf(active.id as string);
      const newIndex = displayIds.indexOf(over.id as string);
      const newOrder = arrayMove(displayIds, oldIndex, newIndex);
      setLocalIds(newOrder);
      reorderDataset(active.id as string, newIndex, displayIds);
    }
    setTimeout(() => setLocalIds(null), 100);
  };

  if (isLoading) {
    return <Skeleton className="h-8 w-full" />;
  }

  if (publishedIds.length === 0) return null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragEnd={handleDragEnd}
    >
      <SortableContext
        items={displayIds}
        strategy={verticalListSortingStrategy}
      >
        <ScrollFade
          className="no-scrollbar -m-1 max-h-50 p-1"
          fromClassName="from-sidebar"
        >
          <SidebarMenu>
            {displayIds.map((id) => (
              <DatasetItem key={id} id={id} />
            ))}
          </SidebarMenu>
        </ScrollFade>
      </SortableContext>
    </DndContext>
  );
};

/**
 * Hover-revealed count badge sitting just left of the row's `…` action.
 * Self-gates: returns null when no blocks reference the dataset. Rows that
 * include this badge get extra right padding via the `data-usage-badge`
 * selector on the menu button so the dataset name truncates with enough
 * room for badge + `…` action.
 */
const DatasetUsageBadge = ({ datasetId }: { datasetId: string }) => {
  const blockIds = useBlocksUsingDataset(datasetId);
  if (blockIds.length === 0) return null;
  return (
    <DatasetUsageTooltip datasetId={datasetId}>
      <button
        type="button"
        data-usage-badge="true"
        aria-label={`${blockIds.length} block${blockIds.length === 1 ? "" : "s"} using this dataset`}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-1.5 right-7 flex h-5 items-center justify-center rounded-sm group-focus-within/menu-item:opacity-100 group-hover/menu-item:opacity-100 data-popup-open:opacity-100 md:opacity-0"
      >
        <Token>{blockIds.length}</Token>
      </button>
    </DatasetUsageTooltip>
  );
};

/** Always-visible variant for the read-only `ActiveDatasetItem` stub. */
const ActiveDatasetUsageBadge = ({ datasetId }: { datasetId: string }) => {
  const blockIds = useBlocksUsingDataset(datasetId);
  if (blockIds.length === 0) return null;
  return (
    <DatasetUsageTooltip datasetId={datasetId}>
      <button
        type="button"
        data-usage-badge="true"
        aria-label={`${blockIds.length} block${blockIds.length === 1 ? "" : "s"} using this dataset`}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        className="absolute top-1.5 right-1 flex h-5 items-center justify-center rounded-sm"
      >
        <Token>{blockIds.length}</Token>
      </button>
    </DatasetUsageTooltip>
  );
};

export const DatasetItem = ({ id }: { id: string }) => {
  const handleLinkClick = useSidebarMobileClick("left");

  const name = useValue(datasets$[id].name);
  const isPrivate = useValue(datasets$[id].private);
  const isVerified = useValue(datasets$[id].verified);
  const avatar = useValue(datasets$[id].avatar);
  const hasPendingChanges = useValue(
    () =>
      normalizeSql(datasets$[id].query.get()) !==
      normalizeSql(datasets$[id].draftQuery.get()),
  );
  const [isRenaming, setIsRenaming] = useState(false);
  const renameOriginalNameRef = useRef<string>("");

  const prefetchHandlers = usePrefetchHandlers(prefetchDatasetRow, id);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const handleClick = (e: React.MouseEvent) => {
    // While renaming, the inline input swaps in for the label and stops its
    // own click propagation; this guard is a safety net so a stray click on
    // the row doesn't navigate away mid-rename.
    if (isRenaming) {
      e.preventDefault();
      return;
    }
    handleLinkClick();
  };

  const handleDoubleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    renameOriginalNameRef.current = name ?? "";
    setIsRenaming(true);
  };

  const handleRenameBlur = () => {
    setIsRenaming(false);
  };

  const handleRenameKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // The row spreads dnd-kit's drag listeners; its KeyboardSensor would
    // otherwise swallow Space (its drag-activation key) before it reaches the
    // input. Stop propagation so typing—spaces included—works during rename.
    e.stopPropagation();
    if (e.key === "Enter") {
      e.currentTarget.blur();
    }
    if (e.key === "Escape") {
      const currentValue = e.currentTarget.value.trim();
      const original = renameOriginalNameRef.current;
      if (currentValue === "") {
        datasets$[id].name.set(original);
      }
      e.currentTarget.blur();
    }
  };

  return (
    <DatasetOptions datasetId={id} type="context">
      <SidebarMenuItem ref={setNodeRef} style={style}>
        <SidebarMenuButtonLink
          name="left"
          to="/datasets/$datasetId"
          params={{ datasetId: id }}
          activeProps={{ isActive: true }}
          onClick={handleClick}
          onDoubleClick={handleDoubleClick}
          className="group-has-[[data-usage-badge=true][data-popup-open]]/menu-item:pr-13 group-hover/menu-item:group-has-data-[usage-badge=true]/menu-item:pr-13 group-focus-within/menu-item:group-has-data-[usage-badge=true]/menu-item:pr-13"
          {...prefetchHandlers}
          {...attributes}
          {...listeners}
        >
          <AvatarDisplay
            avatar={avatar}
            fallbackIcon={isPrivate ? "dataset-magnifying-glass" : "dataset"}
          />
          {isRenaming ? (
            <Input
              value={name ?? ""}
              onChange={(e) => datasets$[id].name.set(e.target.value)}
              onBlur={handleRenameBlur}
              onKeyDown={handleRenameKeyDown}
              placeholder="Untitled dataset"
              className="h-auto min-w-0 flex-1 rounded-none border-0 bg-transparent p-0 text-sm shadow-none focus-visible:border-0 focus-visible:shadow-none focus-visible:ring-0 dark:bg-transparent"
              autoFocus
              onClick={(e) => e.stopPropagation()}
              onDoubleClick={(e) => e.stopPropagation()}
            />
          ) : (
            <span className="truncate">{name || "Untitled dataset"}</span>
          )}
          {isVerified && (
            <VerifiedTooltip datasetId={id}>
              <Icon name="verified-fill" className="size-3" />
            </VerifiedTooltip>
          )}
          {hasPendingChanges && (
            <span
              className="size-1.5 shrink-0 rounded-full bg-muted-foreground"
              title="Pending changes"
            />
          )}
        </SidebarMenuButtonLink>
        <DatasetUsageBadge datasetId={id} />
        <DatasetOptions datasetId={id}>
          <SidebarMenuAction
            showOnHover
            className="rounded-sm data-[state=open]:bg-accent"
          >
            <Icon name="more-horizontal" />
            <span className="sr-only">More</span>
          </SidebarMenuAction>
        </DatasetOptions>
      </SidebarMenuItem>
    </DatasetOptions>
  );
};

/**
 * Read-only "you are here" stub for the active dataset, rendered under the
 * Datasets section header when the section is collapsed. Mirrors DatasetItem's
 * visuals minus drag, rename, and the actions menu.
 */
export const ActiveDatasetItem = ({ id }: { id: string }) => {
  const name = useValue(datasets$[id].name);
  const isPrivate = useValue(datasets$[id].private);
  const isVerified = useValue(datasets$[id].verified);
  const avatar = useValue(datasets$[id].avatar);
  const hasPendingChanges = useValue(
    () =>
      normalizeSql(datasets$[id].query.get()) !==
      normalizeSql(datasets$[id].draftQuery.get()),
  );

  return (
    <SidebarMenuItem>
      <SidebarMenuButtonLink
        name="left"
        to="/datasets/$datasetId"
        params={{ datasetId: id }}
        activeProps={{ isActive: true }}
        className="group-has-data-[usage-badge=true]/menu-item:pr-7"
      >
        <AvatarDisplay
          avatar={avatar}
          fallbackIcon={isPrivate ? "dataset-magnifying-glass" : "dataset"}
        />
        <span className="truncate">{name || "Untitled dataset"}</span>
        {isVerified && (
          <VerifiedTooltip datasetId={id}>
            <Icon name="verified-fill" className="size-3" />
          </VerifiedTooltip>
        )}
        {hasPendingChanges && (
          <span
            className="size-1.5 shrink-0 rounded-full bg-muted-foreground"
            title="Pending changes"
          />
        )}
      </SidebarMenuButtonLink>
      <ActiveDatasetUsageBadge datasetId={id} />
    </SidebarMenuItem>
  );
};
